import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
	putNotificationCenterSettingsV2,
	type SettingsResponseDTO,
	type SettingsUpdateRequestDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponse } from "web_myplann/Infrastructure/Api/ApiUtils";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { NotificationSettingsKeys } from "web_myplann/More/NotificationSettings/Api/notificationSettingsKeys";
import type { NotificationSettingKey } from "web_myplann/More/NotificationSettings/Types/NotificationSettingKey";

type UpdateVariables = {
	key: NotificationSettingKey;
	value: boolean;
};

type MutationContext = {
	previous: SettingsResponseDTO | undefined;
};

const buildRequest = (
	previous: SettingsResponseDTO,
	{ key, value }: UpdateVariables,
): SettingsUpdateRequestDTO => ({
	contractEnd: Boolean(previous.contractEnd?.enabled),
	fixationEnd: Boolean(previous.fixationEnd?.enabled),
	contractAnniversary: Boolean(previous.contractAnniversary?.enabled),
	shortTermContracts: Boolean(previous.shortTermContracts?.enabled),
	[key]: value,
});

export const useUpdateNotificationSettingsMutation = () => {
	const { t } = useResource();
	const queryClient = useQueryClient();
	const queryKey = NotificationSettingsKeys.settings();

	const callApi = processResponse((request: SettingsUpdateRequestDTO) =>
		putNotificationCenterSettingsV2(request),
	);

	return useMutation<
		SettingsResponseDTO,
		Error,
		UpdateVariables,
		MutationContext
	>({
		mutationFn: (variables) => {
			const previous = queryClient.getQueryData<SettingsResponseDTO>(queryKey);
			if (!previous) {
				// The global MutationCache handler toasts `error.message` verbatim,
				// so this must be a translated, user-facing string.
				throw new Error(
					t(Resources.Settings.NotificationSettings.LoadingError),
				);
			}
			return callApi(buildRequest(previous, variables));
		},
		onMutate: async ({ key, value }) => {
			await queryClient.cancelQueries({ queryKey });
			const previous = queryClient.getQueryData<SettingsResponseDTO>(queryKey);

			if (previous) {
				const next: SettingsResponseDTO = {
					...previous,
					[key]: {
						...previous[key],
						enabled: value,
					},
				};
				queryClient.setQueryData<SettingsResponseDTO>(queryKey, next);
			}

			return { previous };
		},
		onSuccess: (data) => {
			// PUT can resolve 404 without throwing; refetch instead of caching a falsy value as "not eligible".
			if (!data) {
				queryClient.invalidateQueries({ queryKey });
				return;
			}
			queryClient.setQueryData<SettingsResponseDTO>(queryKey, data);
		},
		onError: (_error, _variables, context) => {
			if (context?.previous) {
				queryClient.setQueryData(queryKey, context.previous);
			}
			queryClient.invalidateQueries({ queryKey });
		},
	});
};
